/**
 * Fetches AGNT press releases from the WordPress `press-releases` custom post
 * type as structured JSON (not scraped HTML). URLs are used exactly as the WP
 * API returns them — the content origin is `AGNT_INC_CONTENT_PAGE_API_URL`, so
 * links/assets already point at the right host per environment.
 *
 * - `fetchPressReleases` — the archive list. The page does instant client-side
 *   search + pagination over the full archive, so we fetch every release but
 *   only the fields the list renders (`date`, `title`, `slug`), paging at the
 *   REST max of 100 via `X-WP-TotalPages`.
 * - `fetchPressReleaseBySlug` — a single release for the in-app detail page.
 */

import { decode } from 'html-entities';
import { z } from 'zod';

import { normalizeWpBody, rewriteWpAssetUrls } from 'utils/content-pages/strip-wp-chrome';
import { YoastHeadJsonSchema, type YoastHeadJson } from 'utils/content-pages/yoast';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import { AGNT_INC_BASE_URL } from 'constants/domains';

import type { PressRelease, PressReleaseArticle, PressReleaseTeaser } from './types';

// Safety cap so a runaway archive can't fan out unboundedly (100/page).
const MAX_PAGES = 12;

const PressReleaseSchema = z.object({
  date: z.string(),
  title: z.object({ rendered: z.string() }),
  slug: z.string(),
});
const PressReleasesSchema = z.array(PressReleaseSchema);

const ArticleSchema = z.object({
  title: z.object({ rendered: z.string() }),
  excerpt: z.object({ rendered: z.string() }),
  content: z.object({ rendered: z.string() }),
  yoast_head_json: YoastHeadJsonSchema.optional(),
});
const ArticleListSchema = z.array(ArticleSchema);

// WP's auto-generated excerpts append a literal "[&hellip;]" trim marker; once
// decoded that's "[…]" — collapse it to a plain ellipsis rather than leaving
// the brackets in text that was never part of the original article.
function stripTags(html: string): string {
  return decode(html.replace(/<[^>]+>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\s*\[…\]\s*$/, '…');
}

// ---------- Archive list ----------

function listEndpoint(wpOrigin: string, page: number): string {
  return `${wpOrigin}/wp-json/wp/v2/press-releases`
    + `?per_page=100&page=${page}&orderby=date&order=desc&_fields=date,title,slug`;
}

function toPressRelease(item: z.infer<typeof PressReleaseSchema>): PressRelease {
  return { date: item.date, title: decode(item.title.rendered), slug: item.slug };
}

async function fetchPage(url: string): Promise<{ items: PressRelease[]; totalPages: number }> {
  const response = await fetchWithRetry(url);
  if (!response.ok) {
    console.error(`Press releases fetch failed for ${url} with status ${response.status}`);
    return { items: [], totalPages: 0 };
  }
  const totalPages = Number(response.headers.get('x-wp-totalpages') ?? '0') || 0;
  const json: unknown = await response.json();
  const parsed = PressReleasesSchema.safeParse(json);
  if (!parsed.success) {
    console.error(`Press releases response failed schema for ${url}: ${parsed.error.message}`);
    return { items: [], totalPages };
  }
  return { items: parsed.data.map(toPressRelease), totalPages };
}

export async function fetchPressReleases(wpOrigin: string): Promise<PressRelease[]> {
  try {
    const first = await fetchPage(listEndpoint(wpOrigin, 1));
    const lastPage = Math.min(first.totalPages, MAX_PAGES);
    if (lastPage <= 1) return first.items;

    const rest = await Promise.all(
      Array.from({ length: lastPage - 1 }, (_, i) => fetchPage(listEndpoint(wpOrigin, i + 2)))
    );
    return first.items.concat(...rest.map(r => r.items));
  } catch (error: unknown) {
    console.error(`Press releases fetch threw: ${error instanceof Error ? error.message : String(error)}`);
    return [];
  }
}

// ---------- Latest teasers (home page News section) ----------

const PressReleaseTeaserSchema = PressReleaseSchema.extend({
  excerpt: z.object({ rendered: z.string() }),
});
const PressReleaseTeasersSchema = z.array(PressReleaseTeaserSchema);

/**
 * Latest `limit` press releases with excerpts, for summary surfaces like the
 * home News section. Single request (no archive paging) — asks WP for just the
 * newest few, including `excerpt`.
 */
export async function fetchLatestPressReleases(wpOrigin: string, limit = 3): Promise<PressReleaseTeaser[]> {
  const url = `${wpOrigin}/wp-json/wp/v2/press-releases`
    + `?per_page=${limit}&page=1&orderby=date&order=desc&_fields=date,title,slug,excerpt`;
  try {
    const response = await fetchWithRetry(url);
    if (!response.ok) {
      console.error(`Latest press releases fetch failed for ${url} with status ${response.status}`);
      return [];
    }
    const json: unknown = await response.json();
    const parsed = PressReleaseTeasersSchema.safeParse(json);
    if (!parsed.success) {
      console.error(`Latest press releases response failed schema for ${url}: ${parsed.error.message}`);
      return [];
    }
    return parsed.data.map(item => ({
      date: item.date,
      title: decode(item.title.rendered),
      slug: item.slug,
      excerpt: stripTags(item.excerpt.rendered),
    }));
  } catch (error: unknown) {
    console.error(`Latest press releases fetch threw for ${url}: ${error instanceof Error ? error.message : String(error)}`);
    return [];
  }
}

// ---------- Single article ----------

/**
 * Press releases open with an italic "deck" paragraph that the design shows as
 * the hero subtitle. Pull it off the top of the body so it isn't rendered twice;
 * the remaining body keeps the inline dateline + prose.
 */
function extractDeck(contentHtml: string): { deck: string | null; body: string } {
  const match = /^\s*<p>([\s\S]*?)<\/p>/i.exec(contentHtml);
  if (match) {
    const inner = match[1].trim();
    if (/^<em>/i.test(inner) && /<\/em>$/i.test(inner)) {
      return { deck: stripTags(inner), body: contentHtml.slice(match[0].length) };
    }
  }
  return { deck: null, body: contentHtml };
}

// Yoast generates canonical/og_url against the WP origin; rewrite them to the
// production frontend domain so the page doesn't declare a non-canonical URL.
function rewriteSeoUrls(seo: YoastHeadJson, wpOrigin: string): YoastHeadJson {
  const rewrite = (url?: string) =>
    url?.replace(wpOrigin, AGNT_INC_BASE_URL);
  return { ...seo, canonical: rewrite(seo.canonical), og_url: rewrite(seo.og_url) };
}

export async function fetchPressReleaseBySlug(
  wpOrigin: string,
  slug: string
): Promise<{ article: PressReleaseArticle; seo: YoastHeadJson | null } | null> {
  const url = `${wpOrigin}/wp-json/wp/v2/press-releases`
    + `?slug=${encodeURIComponent(slug)}&_fields=title,excerpt,content,yoast_head_json`;
  try {
    const response = await fetchWithRetry(url);
    if (!response.ok) {
      console.error(`Press release fetch failed for ${url} with status ${response.status}`);
      return null;
    }
    const json: unknown = await response.json();
    const parsed = ArticleListSchema.safeParse(json);
    if (!parsed.success) {
      console.error(`Press release response failed schema for ${url}: ${parsed.error.message}`);
      return null;
    }
    const item = parsed.data[0];
    if (!item) return null;

    const { deck, body } = extractDeck(item.content.rendered);
    return {
      article: {
        title: decode(item.title.rendered),
        deck: deck ?? stripTags(item.excerpt.rendered),
        bodyHtml: normalizeWpBody(rewriteWpAssetUrls(body, wpOrigin)),
      },
      seo: item.yoast_head_json ? rewriteSeoUrls(item.yoast_head_json, wpOrigin) : null,
    };
  } catch (error: unknown) {
    console.error(`Press release fetch threw for ${url}: ${error instanceof Error ? error.message : String(error)}`);
    return null;
  }
}
